import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import AwsS3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import useUppyState from '@uppy/react/lib/useUppyState';
import { useDropzone } from 'react-dropzone';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { SSE } from 'sse.js';
import { v4 as uuidv4 } from 'uuid';

import { FileNumberDisclaimer } from './file-number-disclaimer';
import { FilesTable } from './files-table';
import { UploadBreakdownModal } from './upload-breakdown-modal';
import { UploadStatusIndicator } from './upload-status-indicator';
import { FileUploadInput } from './uploader';
import { GenericUppyFile, getUppyAWSS3Options, UploadType } from './uppy';
import { useFileUploadLogic } from './useFileUploadLogic';
import { allowedFileTypes } from '../../../../../backend/src/matter/doc/allowed-extensions';
import { OrgPermission } from '../../../../../backend/src/organisation/organisation-permission';
import { UploadFailureReason } from '../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '../../../analytics/eventsV2';
import { trackCustomEvent } from '../../../analytics/Mixpanel';
import { cn } from '../../lib/utils';
import { Icon } from '../icon';
import { Button } from '../shadcn/button';
import { Skeleton } from '../shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../shadcn/tooltip';
import { useEmailAttachmentWarnings } from '@/api/hooks/useEmailAttachmentWarnings';
import { APIBaseChronos } from '@/api/hosts';
import { useSendPendingFailures, PendingFailure } from '@/api/mutations/useSendPendingFailures';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import { useJobProgress, ActiveJob } from '@/hooks/useJobProgress';

export function FileUploader({
  setFilesProcessing,
  setFilesExist,
  isMatterCreator = false,
}: {
  setFilesProcessing?: (processing: boolean) => void;
  setFilesExist?: (v: boolean) => void;
  isMatterCreator?: boolean;
}) {
  const { matterId } = useParams();
  if (!matterId) return null;

  // Fetch matter data to get allDocumentsCaseId
  const { data: matter } = useGetMatterV2(matterId);
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? null;

  // Use a ref to avoid recreating callbacks when allDocumentsCaseId changes
  // Without this, we make double calls to the upload history endpoint
  const allDocumentsCaseIdRef = useRef<string | null>(null);
  allDocumentsCaseIdRef.current = allDocumentsCaseId;

  // Fetch active jobs for the all documents case
  const { activeJobs } = useJobProgress(allDocumentsCaseId ?? '');

  // TODO: Adjust this based on how many files are currently in the matter
  const maxFiles = 200_000;

  const authInfo = useAuthInfo();
  const { user } = useUserContext();

  const [checkingForDuplicates, setCheckingForDuplicates] = useState(false);
  const [duplicateScanSuccess, setDuplicateScanSuccess] = useState(false);
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [duplicateNumber, setDuplicateNumber] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  // Once true, stays true - controls layout width expansion
  const [hasStartedUpload, setHasStartedUpload] = useState(false);
  // Once true, stays true - tracks if we've ever had active jobs (e.g., Relativity imports)
  const [hasHadActiveJobs, setHasHadActiveJobs] = useState(false);
  const [completedUploadsCount, setCompletedUploadsCount] = useState(0);
  const [totalFilesCount, setTotalFilesCount] = useState(0);
  const [finalUploadedCount, setFinalUploadedCount] = useState(0);

  // Track when a zip upload completed but jobs haven't appeared yet
  const [waitingForZipJob, setWaitingForZipJob] = useState(false);

  const fileIdNameMap = useMemo(() => new Map<string, string>(), []);
  const completedUploads = useMemo(() => new Set<string>(), []);
  const completedUploadsInBatch: UppyFile<{ id?: string }, {}>[] = useMemo(() => [], []);
  const recordUploadedFileToDB = useMemo(
    () => new Worker(new URL('../../../workers/record-matter-doc-upload.ts', import.meta.url), { type: 'module' }),
    [],
  );

  const uploadIdRef = useRef<string | null>(null);
  const pendingFailuresRef = useRef<Array<PendingFailure>>([]);
  const uploadContainsZipRef = useRef<boolean>(false);
  // Track if we had an active zip job that just completed
  const previousActiveJobsRef = useRef<ActiveJob[]>([]);

  const { mutateAsync: sendPendingFailures } = useSendPendingFailures({ matterId });
  const { data: files, refetch: refetchFiles, isLoading: isLoadingFiles } = useGetFiles(matterId);

  // Email attachment warnings - show toast notifications for unsupported email attachments
  useEmailAttachmentWarnings({
    caseId: allDocumentsCaseId ?? '',
    enabled: !!allDocumentsCaseId,
  });

  async function getAccessToken() {
    return await authInfo.tokens.getAccessToken();
  }

  const onApideckSuccess = useCallback(() => {
    refetchFiles();
  }, [refetchFiles]);

  const isApideckEnabled = user?.organisation?.permissions.includes(OrgPermission.Apideck);

  const {
    handleFileAccepted,
    modalOpen,
    setModalOpen,
    modalContent,
    isRelativityModalOpen,
    setIsRelativityModalOpen,
    apiDeckSessionToken,
    apiDeckConnections,
    connectRelativity,
    handleOpenApideckModal,
    handleSelectRelativityList,
  } = useFileUploadLogic({
    subsetId: allDocumentsCaseId,
    isApideckEnabled,
    maxFiles,
    onApideckSuccess,
    activeJobs,
  });

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: true,
    noClick: true,
    onDropAccepted: (acceptedFiles) => handleFileAccepted(acceptedFiles, onUploadUppy),
    maxSize: 3 * 1024 * 1024 * 1024, // 3GB
    maxFiles,
  });

  const [uppy] = useState(() =>
    new Uppy({
      restrictions: {
        maxNumberOfFiles: maxFiles,
        allowedFileTypes,
      },

      allowMultipleUploadBatches: true,

      onBeforeFileAdded: (file) => {
        if (file.name === undefined) {
          return;
        }

        const id = uuidv4();
        const extension = file.name.split('.').length > 1 ? file.name.split('.').pop() : undefined;
        const extensionLowerCase = extension?.toLowerCase();
        const name = `${id}${extensionLowerCase ? `.${extensionLowerCase}` : ''}`;

        fileIdNameMap.set(id, file.name);

        // Make a new File with the correct name as S3 only cares about the File contents' name
        const newFile = new File([file.data], name, { type: file.data.type });

        return { ...file, data: newFile, name, meta: { ...file.meta, name } };
      },
    }).use(AwsS3, getUppyAWSS3Options(getAccessToken, matterId, UploadType.Raw)),
  );

  // SSE Listener for duplicate scan
  async function setupSSEListener(): Promise<void> {
    const accessToken = await getAccessToken();
    const sseResponse = new SSE(
      `${APIBaseChronos}/api/matter/${matterId}/files/duplicate-scan/${allDocumentsCaseIdRef.current}/${uploadIdRef.current}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    sseResponse.onmessage = async (msg: { data: string }) => {
      const parsed: { duplicateNumber: number } = JSON.parse(msg.data);

      refetchFiles();
      setDuplicateNumber(parsed.duplicateNumber);
      setDuplicateScanSuccess(true);
      setCheckingForDuplicates(false);
      // Don't clear uploadId here - the child component needs it to fetch upload history
      // It will be overwritten when the next upload starts
      sseResponse.close();
    };

    sseResponse.onerror = () => {
      refetchFiles();
      setDuplicateScanSuccess(true);
      setCheckingForDuplicates(false);
      // Don't clear uploadId here - the child component needs it to fetch upload history
      // It will be overwritten when the next upload starts
      sseResponse.close();
    };
  }

  // Send upload failures to be recorded using the case-based endpoint with All Documents case
  const sendPendingFailuresCallback = useCallback(async () => {
    if (pendingFailuresRef.current.length > 0 && uploadIdRef.current && allDocumentsCaseIdRef.current) {
      await sendPendingFailures({
        caseId: allDocumentsCaseIdRef.current,
        uploadId: uploadIdRef.current,
        failures: pendingFailuresRef.current,
      });

      // Clear pending failures after sending
      pendingFailuresRef.current = [];
    }
  }, [sendPendingFailures]);

  const requestWorkerUpdateFileDatabase = useCallback(
    async (isFinal = false) => {
      if (window.Worker) {
        const token = await getAccessToken();
        recordUploadedFileToDB.postMessage({
          accessToken: token,
          matterId,
          files: completedUploadsInBatch.map((file) => {
            const id = file.name?.split('.')[0] ?? '';

            return {
              id,
              name: fileIdNameMap.get(id),
              size: file.size,
              extension: file.extension,
            };
          }),
          isFinal,
          uploadId: uploadIdRef.current,
        });

        completedUploadsInBatch.splice(0, completedUploadsInBatch.length);
      }
    },
    [fileIdNameMap, completedUploadsInBatch, recordUploadedFileToDB],
  );

  const handleUppyUploadSuccess = useCallback(
    (file: UppyFile<{ id?: string }, {}> | undefined) => {
      if (file && !completedUploads.has(file.id)) {
        completedUploads.add(file.id);
        completedUploadsInBatch.push(file);

        // Track if any uploaded file is a zip
        if (file.extension === 'zip') {
          uploadContainsZipRef.current = true;
        }

        setCompletedUploadsCount(completedUploads.size);

        // Send every 10 completed docs to the file creation endpoint on the backend
        // which uses a web worker to keep the main thread focused on uploading
        if (completedUploadsInBatch.length > 10) {
          requestWorkerUpdateFileDatabase();
        }
      }
    },
    [completedUploads, completedUploadsInBatch, requestWorkerUpdateFileDatabase],
  );

  const handleUppyUploadComplete = useCallback(async () => {
    await requestWorkerUpdateFileDatabase(true);
    // Wait for worker to complete
    await new Promise((resolve) => (recordUploadedFileToDB.onmessage = resolve));

    await sendPendingFailuresCallback();

    // Store the final count before resetting for display in the completion message
    setFinalUploadedCount(completedUploads.size);

    uppy.resetProgress();
    uppy.removeFiles([...completedUploads].map((id) => id));

    setCompletedUploadsCount(0);
    completedUploads.clear();
    fileIdNameMap.clear();

    setCompletedUploadsCount(0);
    setIsUploading(false);

    // Only start duplicate scan for non-zip uploads
    // For zip uploads, the useEffect monitoring activeJobs will handle it after extraction
    if (!uploadContainsZipRef.current) {
      setCheckingForDuplicates(true);
      setupSSEListener();
    } else {
      // Zip upload completed - waiting for job to appear
      setWaitingForZipJob(true);
    }
    // Reset the flag for next upload
    uploadContainsZipRef.current = false;
  }, [
    uppy,
    fileIdNameMap,
    completedUploads,
    requestWorkerUpdateFileDatabase,
    recordUploadedFileToDB,
    sendPendingFailuresCallback,
  ]);

  const handleUppyRestrictionFailed = useCallback((file: GenericUppyFile | undefined, error: Error) => {
    if (file?.id || file?.name) {
      const id = file.name?.split('.')[0] ?? '';
      const extension = file.name?.split('.').pop() || '';
      const filename = fileIdNameMap.get(id);

      pendingFailuresRef.current.push({
        filename: filename || '',
        extension,
        rejectedReason: UploadFailureReason.UnsupportedFileFormat,
      });
    }

    toast.error(error.message);
  }, []);

  useEffect(() => {
    uppy.off('upload-success', handleUppyUploadSuccess);
    uppy.off('complete', handleUppyUploadComplete);
    uppy.off('restriction-failed', handleUppyRestrictionFailed);

    uppy.on('upload-success', handleUppyUploadSuccess);
    uppy.on('complete', handleUppyUploadComplete);
    uppy.on('restriction-failed', handleUppyRestrictionFailed);
  }, [
    handleUppyUploadComplete,
    handleUppyUploadSuccess,
    requestWorkerUpdateFileDatabase,
    handleUppyRestrictionFailed,
    uppy,
  ]);

  const onUploadUppy = useCallback(
    (files: File[]) => {
      uppy.addFiles(files.map((file) => ({ source: 'local', data: file, name: file.name })));
      uppy.upload();

      setIsUploading(true);
      setTotalFilesCount(files.length);
      setHasStartedUpload(true);

      // Reset duplicate scan state for the new upload
      setDuplicateScanSuccess(false);
      setDuplicateNumber(0);

      // Create an id for the current upload
      uploadIdRef.current = uuidv4();
      // Don't set checkingForDuplicates here - wait for upload to complete
      // For regular files: handleUppyUploadComplete will trigger duplicate scan
      // For zips: useEffect monitoring activeJobs will trigger duplicate scan after extraction
    },
    [uppy],
  );

  const totalProgress = useUppyState(uppy, (state) => state.totalProgress);

  // Used to pass up to parent state the fact that an upload is ongoing
  useEffect(() => {
    setFilesProcessing?.(isUploading || checkingForDuplicates || activeJobs.length > 0);
  }, [isUploading, checkingForDuplicates, activeJobs]);

  useEffect(() => {
    if (files) {
      setFilesExist?.(files.length > 0);
    }
  }, [files, setFilesExist]);

  useEffect(() => {
    const hasActiveJobs = activeJobs && activeJobs.length > 0;
    const hadActiveJobs = previousActiveJobsRef.current.length > 0;

    // Jobs appeared - clear waiting state
    if (hasActiveJobs && waitingForZipJob) {
      setWaitingForZipJob(false);
    }

    // When jobs complete, trigger duplicate scan
    if (!hasActiveJobs && hadActiveJobs) {
      refetchFiles();
      // Only start duplicate scan if we have an uploadId
      if (uploadIdRef.current) {
        setCheckingForDuplicates(true);
        setupSSEListener();
      } else {
        setDuplicateScanSuccess(true);
      }
    }

    // Store current activeJobs for next render
    previousActiveJobsRef.current = activeJobs || [];
  }, [activeJobs, waitingForZipJob]);

  // Track when we first see active jobs - stays true to prevent layout flicker
  useEffect(() => {
    if (activeJobs.length > 0) {
      setHasHadActiveJobs(true);
    }
  }, [activeJobs]);

  // Expand files section once any upload starts, files already exist, or jobs have been active (including Relativity imports)
  const hasFiles = hasStartedUpload || (files !== undefined && files.length > 0) || hasHadActiveJobs;

  return (
    <div className="relative flex h-full overflow-y-hidden focus-visible:outline-none" {...getRootProps()}>
      {/* Dashed border */}
      <div
        className={cn(
          isDragActive ? 'opacity-100' : 'opacity-0',
          'pointer-events-none absolute inset-0 z-20 bg-black/6 duration-150',
        )}
      />
      {/* Upload Section */}
      <div
        className={cn(
          hasFiles ? 'w-1/3' : 'w-full',
          isDragActive ? 'pointer-events-none bg-gray-200/60' : 'bg-gray-100',
          'relative flex min-w-1/3 items-center justify-center border-r px-6 duration-150',
        )}
      >
        <FileUploadInput
          maxFiles={maxFiles}
          matterId={matterId}
          subsetId={allDocumentsCaseId}
          onApideckSuccess={onApideckSuccess}
          isApideckEnabled={isApideckEnabled}
          // Dropzone props
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          open={open}
          // useFileUploadLogic props
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          modalContent={modalContent}
          isRelativityModalOpen={isRelativityModalOpen}
          setIsRelativityModalOpen={setIsRelativityModalOpen}
          apiDeckSessionToken={apiDeckSessionToken}
          apiDeckConnections={apiDeckConnections}
          connectRelativity={connectRelativity}
          handleOpenApideckModal={handleOpenApideckModal}
          handleSelectRelativityList={handleSelectRelativityList}
        />

        <FileNumberDisclaimer />
      </div>

      {/* Files Section */}
      <div className={cn('flex min-w-sm grow flex-col px-6 py-4 pr-4 pb-1', isDragActive && 'pointer-events-none')}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-serif text-2xl font-medium">Files</h2>
            <Tooltip>
              <TooltipTrigger className="-mb-0.5 pl-1 text-gray-400">
                <Icon className="lucide--info" />
              </TooltipTrigger>
              <TooltipContent side="right">
                These files have been uploaded, but not yet processed by Wexler.
              </TooltipContent>
            </Tooltip>
          </div>

          <Button
            variant="default"
            size="sm"
            onClick={() => {
              if (isMatterCreator) {
                setFlowModalOpen(true);
                trackCustomEvent(TrackingEventsV2.FileHistoryOpenBreakdownModal, { matterId });
              } else {
                trackCustomEvent(TrackingEventsV2.FileHistoryOpenBreakdownLink, { matterId });
                window.open(`/matters/${matterId}/settings/file-history`, '_blank');
              }
            }}
          >
            <Icon className="lucide--git-branch" />
            View Upload Breakdown
          </Button>
        </div>

        <UploadStatusIndicator
          isUploading={isUploading}
          uploadProgress={totalProgress}
          uploadedCount={isUploading ? completedUploadsCount : finalUploadedCount}
          totalCount={totalFilesCount}
          isScanning={checkingForDuplicates && !duplicateScanSuccess}
          scanComplete={duplicateScanSuccess}
          duplicateCount={duplicateNumber}
          activeJobs={activeJobs}
          waitingForJob={waitingForZipJob}
          matterId={matterId}
          uploadId={uploadIdRef.current}
        />

        {hasFiles ? (
          <FilesTable files={files ?? []} isLoading={isLoadingFiles} />
        ) : isLoadingFiles ? (
          <Skeleton className="mt-2 h-4 w-16" />
        ) : (
          <p className="mt-2 text-gray-500">No files added yet</p>
        )}
      </div>

      <UploadBreakdownModal open={flowModalOpen} onOpenChange={setFlowModalOpen} matterId={matterId} />
    </div>
  );
}
